import { useEffect, useMemo, useState, type MouseEvent } from 'react'
import { Panel, PanelGroup } from 'react-resizable-panels'
import { Eye, FileText, Folder, LayoutGrid, Building2, User, FilePlus, FolderPlus } from 'lucide-react'
import { getWorkspaceIcon } from '@/lib/workspace-icons'
import { GoogleDriveIcon } from '@/components/icons/google-drive-icon'
import { Button } from '@/components/ui/button'
import { fetchWithAuth } from '@/lib/api'
import { useWorkspace } from '@/providers/workspace-provider'
import type { Workspace } from '@/providers/workspace-provider'
import { useWorkspaceExtensions } from '@/hooks/use-workspace-extensions'
import { useSpaces } from '@/hooks/use-spaces'
import { documentsRootPathForWorkspace } from '@/lib/workspace-documents-root'
import { testIds } from '@/lib/test-ids'
import { getIntegrationById } from '@/config/integrations'
import { WorkspaceChipRow } from '@/components/features/workspace-chip-row'
import {
  HomeFeedList,
  HomeFeedRow,
  HomeWidgetShell,
} from '@/components/dashboard/home-widget-shell'
import { HomeFeedContextMenu } from '@/components/dashboard/home-feed-context-menu'
import { DocumentPreviewPane } from '@/components/features/document-list-preview-pane'
import { PreviewPaneHeader } from '@/components/features/preview-pane-header'
import type { SelectedItem } from '@/components/features/document-list.types'
import { PreviewPanelToggle } from '@/components/integrations/_shared/preview-panel-toggle'
import { SecondaryPreviewResizeHandle } from '@/components/integrations/_shared/secondary-preview-resize-handle'
import { SecondaryPanel } from '@/components/layout/secondary-panel'
import { PANEL_SIZES } from '@/lib/panel-sizes'
import { WS_ICON_ON_ACCENT, workspaceAccentFill } from '@/lib/workspace-color'

interface RecentDoc {
  id: string
  title: string
  filePath: string
  updatedAtRaw: string
}

interface FolderNode {
  name: string
  path: string
  children?: FolderNode[]
}

type DashSelection =
  | { kind: 'document'; doc: RecentDoc }
  | { kind: 'folder'; folder: FolderNode }

const WORKSPACE_HOME_PREVIEW_KEY = 'maater.workspace-home.previewOpen'

function readPreviewOpen(): boolean {
  try {
    return sessionStorage.getItem(WORKSPACE_HOME_PREVIEW_KEY) === '1'
  } catch {
    return false
  }
}

function writePreviewOpen(open: boolean) {
  try {
    sessionStorage.setItem(WORKSPACE_HOME_PREVIEW_KEY, open ? '1' : '0')
  } catch {
    /* ignore */
  }
}

function WsIcon({ ws }: { ws: Workspace }) {
  const fallback = ws.name.toLowerCase().includes('personal') ? User : Building2
  const Icon = getWorkspaceIcon(ws.icon, fallback)
  return <Icon className="w-5 h-5" style={{ color: WS_ICON_ON_ACCENT }} />
}

interface WorkspaceHomeDashboardProps {
  workspaceId: string
  onDocumentClick?: (documentId: string, event?: React.MouseEvent) => void
  /** Same handler as Browse sidebar folder rows (`handleProjectClick`). */
  onFolderClick?: (folderPath: string, skipTabManagementOrEvent?: boolean | React.MouseEvent) => void
}

const EXTENSION_ICON_FALLBACKS: Record<string, React.ElementType> = {
  'google-drive': GoogleDriveIcon,
}

function ExtensionRowIcon({ extensionId }: { extensionId: string }) {
  const integration = getIntegrationById(extensionId)
  const Icon = integration?.icon ?? EXTENSION_ICON_FALLBACKS[extensionId] ?? LayoutGrid
  return <Icon className="w-4 h-4 flex-none text-muted-foreground" />
}

function isModClick(event?: MouseEvent) {
  return Boolean(event?.metaKey || event?.ctrlKey)
}

export function WorkspaceHomeDashboard({ workspaceId, onDocumentClick, onFolderClick }: WorkspaceHomeDashboardProps) {
  const { workspaces } = useWorkspace()
  const { extensions: wsExtensionList } = useWorkspaceExtensions(workspaceId)
  const ws = workspaces.find(w => w.id === workspaceId)
  const { spaces } = useSpaces(workspaceId)

  const documentsRootPath = useMemo(
    () => (ws ? documentsRootPathForWorkspace(ws, spaces) : ''),
    [ws, spaces],
  )

  const triggerNewDoc = () => {
    if (!documentsRootPath) return
    window.dispatchEvent(
      new CustomEvent('create-file-at-path', {
        detail: { folderPath: documentsRootPath, workspaceId },
      }),
    )
  }
  const triggerNewFolder = () => {
    if (!documentsRootPath) return
    window.dispatchEvent(
      new CustomEvent('create-folder-at-path', { detail: { parentPath: documentsRootPath } }),
    )
  }

  const [recentDocs, setRecentDocs] = useState<RecentDoc[]>([])
  const [folders, setFolders] = useState<FolderNode[]>([])
  const [loading, setLoading] = useState(true)
  const [selection, setSelection] = useState<DashSelection | null>(null)
  const [previewOpen, setPreviewOpen] = useState(readPreviewOpen)
  const [contextMenu, setContextMenu] = useState<
    | { x: number; y: number; selection: DashSelection }
    | null
  >(null)

  const togglePreview = () => {
    setPreviewOpen((prev) => {
      const next = !prev
      writePreviewOpen(next)
      return next
    })
  }

  const pinPreview = () => {
    setPreviewOpen(true)
    writePreviewOpen(true)
  }

  useEffect(() => {
    if (!contextMenu) return
    const handleKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setContextMenu(null)
    }
    const handleClick = () => setContextMenu(null)
    document.addEventListener('keydown', handleKey)
    document.addEventListener('mousedown', handleClick)
    return () => {
      document.removeEventListener('keydown', handleKey)
      document.removeEventListener('mousedown', handleClick)
    }
  }, [contextMenu])

  useEffect(() => {
    if (!ws?.contentRoot) { setLoading(false); return }
    setLoading(true)
    setSelection(null)

    Promise.all([
      fetchWithAuth(`/api/documents/recent?limit=8&sortBy=edited&workspaceId=${encodeURIComponent(workspaceId)}`).then(r => r.json()).catch(() => ({ documents: [] })),
      // DB-first folder list (same as Browse); /api/folders is board/archive only, no ?path= listing
      fetchWithAuth(
        `/api/documents/explorer-subfolders?path=${encodeURIComponent(ws.contentRoot.replace(/\/$/, ''))}`
      )
        .then(async (r) => (r.ok ? r.json() : { folders: [] }))
        .catch(() => ({ folders: [] })),
    ]).then(([docsData, foldersData]) => {
      setRecentDocs(docsData.documents ?? [])
      setFolders(foldersData.folders ?? [])
      setLoading(false)
    })
  }, [workspaceId, ws?.contentRoot])

  if (!ws) {
    return (
      <div className="h-full flex items-center justify-center text-muted-foreground text-sm">
        Workspace not found
      </div>
    )
  }

  const wsColor = ws.color ?? 'hsl(var(--primary))'
  const enabledExtensions = wsExtensionList.filter(e => e.enabled && e.extension_id !== 'voice-memos')

  const EXTENSION_LABELS: Record<string, string> = {
    basecamp: 'Basecamp',
    'google-drive': 'Google Drive',
    'product-board': 'Product Board',
    quiz: 'Quiz & LMS',
    'knowledge-hub': 'Knowledge Hub',
    zoom: 'Zoom',
    nextcloud: 'Nextcloud',
  }

  const formatDate = (dateStr?: string) => {
    if (!dateStr) return ''
    const d = new Date(dateStr)
    if (Number.isNaN(d.getTime())) return ''
    const now = new Date()
    const diffMs = now.getTime() - d.getTime()
    const diffH = Math.floor(diffMs / 3600000)
    if (diffH < 1) return 'Just now'
    if (diffH < 24) return `${diffH}h ago`
    const diffD = Math.floor(diffH / 24)
    if (diffD === 1) return 'Yesterday'
    if (diffD < 7) return `${diffD} days ago`
    return d.toLocaleDateString()
  }

  const openDocument = (doc: RecentDoc, event?: MouseEvent) => {
    onDocumentClick?.(doc.id, event)
  }

  const openFolder = (folder: FolderNode, event?: MouseEvent) => {
    onFolderClick?.(folder.path, event)
  }

  const selectDocument = (doc: RecentDoc, event?: MouseEvent) => {
    setSelection({ kind: 'document', doc })
    if (isModClick(event)) openDocument(doc, event)
  }

  const selectFolder = (folder: FolderNode, event?: MouseEvent) => {
    setSelection({ kind: 'folder', folder })
    if (isModClick(event)) openFolder(folder, event)
  }

  const previewItem: SelectedItem | null = selection
    ? selection.kind === 'document'
      ? {
          kind: 'document',
          doc: {
            id: selection.doc.id,
            title: selection.doc.title,
            type: 'note',
            excerpt: '',
            updatedAt: '',
            updatedBy: '',
            tags: [],
            filePath: selection.doc.filePath,
          },
        }
      : {
          kind: 'folder',
          name: selection.folder.name,
          path: selection.folder.path,
        }
    : null

  const ids = testIds.workspaceHome

  const dashboardBody = (
    <div className="h-full overflow-y-auto bg-background">
      <div
        className="px-8 pt-8 pb-6 border-b border-border"
        style={{ borderTopWidth: 3, borderTopColor: wsColor }}
      >
        <div className="flex items-start justify-between">
          <div className="flex items-center gap-3">
            <span
              className="w-10 h-10 rounded-lg flex items-center justify-center flex-none"
              style={{ backgroundColor: workspaceAccentFill(wsColor) }}
            >
              <WsIcon ws={ws} />
            </span>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">{ws.name}</h1>
              <p className="text-sm text-muted-foreground mt-0.5">
                {loading ? 'Loading…' : `${recentDocs.length > 0 ? `${recentDocs.length} recent docs` : 'No recent docs'}${enabledExtensions.length > 0 ? ` · ${enabledExtensions.length} extension${enabledExtensions.length !== 1 ? 's' : ''}` : ''}`}
              </p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <PreviewPanelToggle
              open={previewOpen}
              onToggle={togglePreview}
              testId={ids.previewToggle}
            />
            <div className="flex items-center gap-1.5 text-xs text-muted-foreground/60 border border-dashed border-border rounded-md px-3 py-1.5">
              <LayoutGrid className="w-3 h-3" />
              <span>Customize</span>
            </div>
          </div>
        </div>

        <div className="mt-4">
          <WorkspaceChipRow excludeWorkspaceId={workspaceId} />
        </div>
      </div>

      <div className="px-8 py-6 grid grid-cols-3 gap-6">
        <div className="col-span-2 space-y-6">
          {contextMenu ? (
            <HomeFeedContextMenu
              x={contextMenu.x}
              y={contextMenu.y}
              onClose={() => setContextMenu(null)}
              onOpen={() => {
                if (contextMenu.selection.kind === 'document') {
                  openDocument(contextMenu.selection.doc)
                } else {
                  openFolder(contextMenu.selection.folder)
                }
              }}
              onPreview={() => {
                setSelection(contextMenu.selection)
                pinPreview()
              }}
              addToChat={
                contextMenu.selection.kind === 'document'
                  ? {
                      id: contextMenu.selection.doc.id,
                      title: contextMenu.selection.doc.title,
                      path: contextMenu.selection.doc.filePath,
                    }
                  : null
              }
              filePath={
                contextMenu.selection.kind === 'document'
                  ? contextMenu.selection.doc.filePath
                  : contextMenu.selection.folder.path
              }
              workspaces={workspaces}
            />
          ) : null}

          <HomeWidgetShell title="Recent Documents">
            {loading ? (
              <div className="space-y-2">
                {[1, 2, 3].map(i => (
                  <div key={i} className="h-9 rounded-md bg-muted/40 animate-pulse" />
                ))}
              </div>
            ) : recentDocs.length > 0 ? (
              <HomeFeedList>
                {recentDocs.map(doc => (
                  <HomeFeedRow
                    key={doc.id}
                    title={doc.title || doc.filePath?.split('/').pop()}
                    trailing={formatDate(doc.updatedAtRaw)}
                    leading={<FileText className="h-4 w-4" />}
                    selected={selection?.kind === 'document' && selection.doc.id === doc.id}
                    onClick={(e) => selectDocument(doc, e)}
                    onDoubleClick={(e) => {
                      selectDocument(doc, e)
                      openDocument(doc, e)
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      selectDocument(doc, e)
                      setContextMenu({ x: e.clientX, y: e.clientY, selection: { kind: 'document', doc } })
                    }}
                  />
                ))}
              </HomeFeedList>
            ) : (
              <div className="rounded-md border border-border bg-muted/15 px-3 py-4 space-y-3">
                <p className="text-sm text-muted-foreground/80">No recent documents in this workspace.</p>
                {documentsRootPath ? (
                  <div className="flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="default"
                      size="sm"
                      data-testid={ids.newDocument}
                      onClick={triggerNewDoc}
                    >
                      <FilePlus className="h-4 w-4 mr-1.5" />
                      New document
                    </Button>
                    <Button
                      type="button"
                      variant="outline"
                      size="sm"
                      data-testid={ids.newFolder}
                      onClick={triggerNewFolder}
                    >
                      <FolderPlus className="h-4 w-4 mr-1.5" />
                      New folder
                    </Button>
                  </div>
                ) : (
                  <p className="text-xs text-muted-foreground/70">
                    Set a content root for this workspace in Settings to create documents here.
                  </p>
                )}
              </div>
            )}
          </HomeWidgetShell>

          {!loading && folders.length > 0 && (
            <HomeWidgetShell title="Folders">
              <HomeFeedList>
                {folders.map(folder => (
                  <HomeFeedRow
                    key={folder.path}
                    title={folder.name}
                    trailing={
                      folder.children
                        ? `${folder.children.length} items`
                        : undefined
                    }
                    leading={<Folder className="h-4 w-4" style={{ color: wsColor }} />}
                    selected={selection?.kind === 'folder' && selection.folder.path === folder.path}
                    onClick={(e) => selectFolder(folder, e)}
                    onDoubleClick={(e) => {
                      selectFolder(folder, e)
                      openFolder(folder, e)
                    }}
                    onContextMenu={(e) => {
                      e.preventDefault()
                      selectFolder(folder, e)
                      setContextMenu({ x: e.clientX, y: e.clientY, selection: { kind: 'folder', folder } })
                    }}
                  />
                ))}
              </HomeFeedList>
            </HomeWidgetShell>
          )}
        </div>

        <div className="space-y-5">
          {enabledExtensions.length > 0 && (
            <HomeWidgetShell title="Extensions">
              <HomeFeedList>
                {enabledExtensions.map(ext => {
                  const tabId =
                    ext.extension_id === 'product-board' ? 'integration-product-board' : ext.extension_id
                  return (
                    <HomeFeedRow
                      key={ext.extension_id}
                      title={EXTENSION_LABELS[ext.extension_id] ?? ext.extension_id}
                      leading={<ExtensionRowIcon extensionId={ext.extension_id} />}
                      onClick={() => window.dispatchEvent(new CustomEvent('integration-view-change', {
                        detail: {
                          tabId,
                          title: EXTENSION_LABELS[ext.extension_id] ?? ext.extension_id,
                          type: `integration-${ext.extension_id}`,
                          workspaceId,
                          integrationData: { integrationId: ext.extension_id },
                        },
                      }))}
                    />
                  )
                })}
              </HomeFeedList>
            </HomeWidgetShell>
          )}

          <div className="p-3 rounded-lg border border-dashed border-border bg-muted/20 text-xs text-muted-foreground/70">
            <p className="font-medium mb-1">Workspace dashboard</p>
            <p>Customizable per workspace. Planned widgets: Board View, Basecamp Progress, File List, AI Flows.</p>
          </div>
        </div>
      </div>
    </div>
  )

  return (
    <div className="h-full min-h-0 overflow-hidden bg-background" data-testid={ids.root}>
      {previewOpen ? (
        <PanelGroup direction="horizontal" className="h-full min-h-0">
          <Panel defaultSize={100 - PANEL_SIZES.SECONDARY_DEFAULT} minSize={40} className="min-w-0">
            {dashboardBody}
          </Panel>
          <SecondaryPreviewResizeHandle />
          <SecondaryPanel
            panelType="workspace-home-preview"
            panelId="workspace-home-preview"
            defaultSize={PANEL_SIZES.SECONDARY_DEFAULT}
            minSize={PANEL_SIZES.SECONDARY_MIN}
            background="card"
            className="min-w-0 overflow-hidden"
          >
            <div className="h-full min-h-0" data-testid={ids.previewPane}>
              {previewItem ? (
                <DocumentPreviewPane
                  item={previewItem}
                  workspaceColor={ws.color ?? undefined}
                  onOpen={(event) => {
                    if (selection?.kind === 'document') openDocument(selection.doc, event)
                    if (selection?.kind === 'folder') openFolder(selection.folder, event)
                  }}
                  onClose={() => {
                    setPreviewOpen(false)
                    writePreviewOpen(false)
                  }}
                />
              ) : (
                <div className="flex h-full min-h-0 flex-col" data-testid={ids.previewEmpty}>
                  <PreviewPaneHeader
                    row
                    icon={<Eye className="h-4 w-4" />}
                    label="PREVIEW"
                    onClose={() => {
                      setPreviewOpen(false)
                      writePreviewOpen(false)
                    }}
                  />
                  <p className="p-4 text-sm text-muted-foreground">
                    Select a document or folder to preview, or double-click to open.
                  </p>
                </div>
              )}
            </div>
          </SecondaryPanel>
        </PanelGroup>
      ) : (
        dashboardBody
      )}
    </div>
  )
}
